import React, { useEffect, useState, useCallback } from "react";
import stdSchema from "../schemas/standard.json";
import DynamicForm from "../components/DynamicForm/DynamicForm";
import DataTable from "../components/DataTable/DataTable";
import { apiGet, apiPost, apiPut } from "../services/api";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";

const autoSx = {
  "& .MuiOutlinedInput-root": { padding: "4px 32px 4px 14px !important", fontSize: 12 },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "0 !important", fontSize: 12 },
  "& .MuiAutocomplete-popupIndicator": { padding: "1px", marginRight: "-2px" },
  "& .MuiAutocomplete-clearIndicator": { padding: "1px" },
};
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import * as permissions from "./app_props";

export default function StandardsPage() {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const [stds, setStds] = useState([]);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {
      // ignore
    }
    return 50;
  })();
  const [types, setTypes] = useState([]);
  const [compliances, setCompliances] = useState([]);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [filters, setFilters] = useState({});
  const [pageInfo, setPageInfo] = useState({
    page: 0,
    size: defaultSize,
    total: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const buildFilterQuery = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(`filter.${key}`, value);
      }
    });
    return params.toString();
  }, [filters]);

  const load = useCallback(
    async (page = pageInfo.page, size = pageInfo.size) => {
      try {
        const query = buildFilterQuery();
        const rdata = await apiGet(
          `/api/crs?type=standard&page=${page}&size=${size}${query ? `&${query}` : ""}`,
        );
        const content = Array.isArray(rdata) ? rdata : rdata.content || [];
        const mapped = content.map((it) => ({
          ...it,
          standardName: it.name || it.title,
          media: it.media,
          source: it.source,
          scope: it.scope,
          location: it.location,
          sme: it.sme,
          sourceNo: it.sourceNo,
          primaryContact: it.primaryContact,
        }));
        setStds(mapped);
        if (rdata && typeof rdata.totalElements !== "undefined") {
          setPageInfo((cur) => ({
            ...cur,
            total: rdata.totalElements,
            page,
            size,
          }));
        } else {
          setPageInfo((cur) => ({ ...cur, page, size }));
        }
      } catch (err) {
        console.error("Failed to load standards or types", err);
      }
    },
    [buildFilterQuery, pageInfo.page, pageInfo.size],
  );

  // load compliance types and compliances once on mount
  useEffect(() => {
    let mounted = true;
    (async function () {
      try {
        const [tdata, cdata] = await Promise.all([
          apiGet("/api/compliance-types"),
          apiGet("/api/compliances"),
        ]);
        if (!mounted) return;
        setTypes(Array.isArray(tdata) ? tdata : tdata.content || []);
        setCompliances(Array.isArray(cdata) ? cdata : cdata.content || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load compliance types", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageSizeChange = useCallback(
    (newSize) => {
      try {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
      } catch (e) {
        // ignore
      }
      setPageInfo((p) => ({ ...p, page: 0, size: newSize }));
    },
    [load],
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setPageInfo((p) => ({ ...p, page: newPage }));
    },
    [load],
  );

  async function handleSave(formValues) {
    try {
      const general = formValues.general || {};
      const admin = formValues.admin || {};

      // find compliance type id for standard
      const stdType =
        types.find((t) => (t.type || "").toLowerCase() === "standard") ||
        types[0];

      const payload = {
        title: general.standardName || general.name,
        name: general.standardName || general.name,
        media: general.media,
        location: general.location,
        source: general.source,
        scope: general.scope,
        sme: general.sme,
        sourceNo: general.sourceNo,
        primaryContact: general.primaryContact,
        enabled: true,
        complianceType: stdType ? { id: stdType.id } : null,
        compliance: selectedCompliance ? { id: selectedCompliance.id } : null,
      };
      // include modifiedAt when editing so backend can validate concurrency
      if (admin && admin.dateModified) {
        payload.modifiedAt = admin.dateModified;
      }

      const existingId = formValues._id || formValues.id || null;
      await (existingId
        ? await apiPut(`/api/crs/${existingId}`, payload)
        : await apiPost(`/api/crs`, payload));

      alert("Standard saved");
      await load();
      navigate("/standards");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert("Save failed: " + err.message);
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Standards</Typography>
                    <div>
                      {user &&
                        [permissions.COMPLIANCE_CAN_ADD_STANDARDS].some(
                          (authority) =>
                            user.authorities === undefined
                              ? false
                              : user.authorities.includes(authority),
                        ) && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => { setSelectedCompliance(null); navigate("/standards/new"); }}
                          >
                            Add New Standard
                          </Button>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={stdSchema}
                      rows={stds}
                      onFiltersChange={setFilters}
                      onPageSizeChange={handlePageSizeChange}
                      extraControls={null}
                      totalCount={pageInfo.total}
                      currentPage={pageInfo.page}
                      onPageChange={handlePageChange}
                      onEdit={(idx, row) => {
                        const defaultValues = {
                          _id: row._id || row.id || null,
                          general: {
                            standardName: row.standardName || row.name,
                            media: row.media,
                            source: row.source,
                            scope: row.scope,
                            location: row.location,
                            sme: row.sme,
                            sourceNo: row.sourceNo,
                            primaryContact: row.primaryContact,
                          },
                          requirements: row.requirements
                            ? row.requirements
                            : [],
                          admin: {
                            dateModified: row.modifiedAt,
                          },
                        };
                        if (row.compliance) {
                          const match = compliances.find((c) => c.id === row.compliance.id);
                          setSelectedCompliance(match || row.compliance);
                        } else {
                          setSelectedCompliance(null);
                        }
                        navigate(`/standards/new`, {
                          state: { defaultValues },
                        });
                      }}
                      onDelete={(idx) => {
                        // deletion handled via API not implemented here
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
        <Route
          path="new"
          element={
            <Box sx={{ m: 1, maxHeight: "87vh", overflowY: "auto" }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">New Standard</Typography>
                    <div />
                  </div>
                  <div
                    style={{ marginTop: 12 }}
                    className="flex justify-center"
                  >
                    <div style={{ width: "100%", maxWidth: 900 }}>
                      <div style={{ maxWidth: 560, margin: "0 auto 4px auto" }}>
                        <div className="grid grid-cols-[160px_1fr] items-center gap-2 py-px">
                          <span className="text-xs text-gray-600 whitespace-nowrap">Compliance</span>
                          <Autocomplete
                            sx={autoSx}
                            options={compliances}
                            getOptionLabel={(o) => (o ? o.name : "")}
                            value={selectedCompliance}
                            onChange={(_, v) => setSelectedCompliance(v)}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            fullWidth
                            clearOnEscape
                            renderInput={(params) => (
                              <TextField {...params} size="small" margin="dense" />
                            )}
                            ListboxProps={{ style: { maxHeight: 240 } }}
                          />
                        </div>
                      </div>
                      <DynamicForm
                        schema={stdSchema}
                        defaultValues={location.state?.defaultValues || {}}
                        onSubmit={handleSave}
                        onCancel={() => navigate("/standards")}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>
    </div>
  );
}
